import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 */
class DeferredMedia extends Component {
  isPlaying = false;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    // Initialize custom video UI
    this.initDetailsContentVideos();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Initialize .details-content videos with custom play/pause & fullscreen
   */
  initDetailsContentVideos() {
    const videos = this.querySelectorAll('.details-content video');

    videos.forEach(video => {
      video.loop = true;
      video.muted = false;
      video.controls = false;
      video.playsInline = true;
      video.parentElement.style.position = 'relative';

      // Remove old controls if exist
      const oldControls = video.parentElement.querySelector('.custom-video-controls');
      if (oldControls) oldControls.remove();

      // Create controls wrapper
      const controls = document.createElement('div');
      controls.classList.add('custom-video-controls');

      const playPauseBtn = document.createElement('div');
      playPauseBtn.classList.add('video-btn', 'play-btn', 'paused'); // start as paused

      const fullscreenBtn = document.createElement('div');
      fullscreenBtn.classList.add('video-btn', 'fullscreen-btn');

      controls.appendChild(playPauseBtn);
      controls.appendChild(fullscreenBtn);
      video.parentElement.appendChild(controls);

      // --- EVENTS ---

      // Toggle play/pause when button clicked
      playPauseBtn.addEventListener('click', (e) => {
        e.stopPropagation();

        // 🔥 Pause all other videos inside same .details-content
        const parent = video.closest('.details-content');
        if (parent) {
          parent.querySelectorAll('video').forEach(v => {
            if (v !== video) v.pause();
          });
        }

        // Play or pause current video
        if (video.paused) {
          video.muted = false;
          video.play();
        } else {
          video.pause();
        }
      });

      // 🔥 Also pause others in same parent if video auto-plays or plays by script
      video.addEventListener('play', () => {
        const parent = video.closest('.details-content');
        if (parent) {
          parent.querySelectorAll('video').forEach(v => {
            if (v !== video) v.pause();
          });
        }
        playPauseBtn.classList.remove('paused');
        playPauseBtn.classList.add('playing');
        controls.style.opacity = 1;
      });

      video.addEventListener('pause', () => {
        playPauseBtn.classList.remove('playing');
        playPauseBtn.classList.add('paused');
        controls.style.opacity = 1;
      });

      video.addEventListener('ended', () => {
        playPauseBtn.classList.remove('playing');
        playPauseBtn.classList.add('paused');
        controls.style.opacity = 1;
      });

      // Fullscreen handler
      fullscreenBtn.addEventListener('click', (e) => {
          e.stopPropagation();

          // If already in fullscreen, exit it
          if (document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement) {
              if (document.exitFullscreen) {
                  document.exitFullscreen();
              } else if (document.webkitExitFullscreen) { // Safari
                  document.webkitExitFullscreen();
              } else if (document.msExitFullscreen) { // IE/Edge
                  document.msExitFullscreen();
              }
          } else {
              // Enter fullscreen
              if (video.requestFullscreen) {
                  video.requestFullscreen();
              } else if (video.webkitEnterFullscreen) { // iOS Safari
                  video.webkitEnterFullscreen();
              } else if (video.webkitRequestFullscreen) { // macOS Safari
                  video.webkitRequestFullscreen();
              } else if (video.msRequestFullscreen) { // IE/Edge
                  video.msRequestFullscreen();
              } else {
                  console.log('Fullscreen API is not supported on this browser.');
              }
          }
      });

      // Show fullscreen button only when visible
      const toggleFullscreenBtn = () => {
        const rect = video.getBoundingClientRect();
        const fullyVisible =
          rect.top >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
        fullscreenBtn.style.display = fullyVisible ? 'block' : 'none';
      };

      window.addEventListener('scroll', toggleFullscreenBtn);
      window.addEventListener('resize', toggleFullscreenBtn);
      toggleFullscreenBtn();

      // Ensure icons visible initially
      controls.style.opacity = 1;
      playPauseBtn.classList.add('paused');
    });
  }

  /**
   * Default theme logic
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs?.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));
    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);
    if (focus && content instanceof HTMLElement) content.focus();

    this.refs?.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // 🔥 Pause others in same .details-content if autoplayed
      const parent = content.closest('.details-content');
      if (parent) {
        parent.querySelectorAll('video').forEach(v => {
          if (v !== content) v.pause();
        });
      }
      content.muted = false;
      content.play();
    }

    // Re-init custom controls after load
    this.initDetailsContentVideos();
  }

  toggleMedia() {
    if (this.isPlaying) this.pauseMedia();
    else this.playMedia();
  }

  playMedia() {
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        // 🔥 Pause all others inside same .details-content
        const parent = video.closest('.details-content');
        if (parent) {
          parent.querySelectorAll('video').forEach(v => {
            if (v !== video) v.pause();
          });
        }
        video.muted = false;
        video.play();
      }
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  pauseMedia() {
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"pauseVideo","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * Product Model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();
    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  async setupModelViewerUI(errors) {
    if (errors) return;
    if (!Shopify.ModelViewerUI) await this.#waitForModelViewerUI();
    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;
    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    let pointerStartX = 0;
    let pointerStartY = 0;
    element.addEventListener('pointerdown', e => {
      pointerStartX = e.clientX;
      pointerStartY = e.clientY;
    }, { signal });

    element.addEventListener('click', e => {
      const dx = Math.abs(e.clientX - pointerStartX);
      const dy = Math.abs(e.clientY - pointerStartY);
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < 10) this.pauseMedia();
    }, { signal });
  }

  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;
    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) return;
      await new Promise(r => setTimeout(r, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
